import { ChangeDetectionStrategy, Component, computed, input, output, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { TranslatePipe } from '@ngx-translate/core';
import { AppIcon } from '@app/shared/controls/app-icon/app-icon';
import { PasswordStrengthComponent } from '@app/shared/controls/password-strength/password-strength.component';

type ChangePasswordModel = {
  currentPassword: string;
  newPassword: string;
};

@Component({
  selector: 'app-change-password',
  imports: [AppIcon, FormsModule, TranslatePipe, PasswordStrengthComponent],
  templateUrl: './change-password.component.html',
  styleUrl: './change-password.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class ChangePasswordComponent {
  readonly model = input.required<ChangePasswordModel>();
  readonly save = output<void>();
  readonly cancelled = output<void>();

  protected readonly confirmPassword = signal('');
  protected readonly showCurrentPassword = signal(false);
  protected readonly showNewPassword = signal(false);
  protected readonly showConfirmPassword = signal(false);
  protected readonly passwordsMismatch = computed(() => {
    const confirm = this.confirmPassword().trim();
    return confirm.length > 0 && this.model().newPassword !== confirm;
  });

  protected readonly passwordMeetsRequirements = computed(() => {
    const password = this.model().newPassword;
    return password.length >= 8
      && /[A-Z]/.test(password)
      && /\d/.test(password)
      && /[!"#$%&'()*+,\-./:;<=>?@[\\\]^_`{|}~]/.test(password);
  });

  protected readonly canSubmit = computed(() => {
    return !this.passwordsMismatch()
      && this.model().currentPassword.trim().length > 0
      && this.passwordMeetsRequirements();
  });

  protected onCurrentPasswordChange(value: string): void {
    this.model().currentPassword = value;
  }

  protected onNewPasswordChange(value: string): void {
    this.model().newPassword = value;
  }

  protected onConfirmPasswordChange(value: string): void {
    this.confirmPassword.set(value);
  }

  protected toggleCurrentPasswordVisibility(): void {
    this.showCurrentPassword.update((isVisible) => !isVisible);
  }

  protected toggleNewPasswordVisibility(): void {
    this.showNewPassword.update((isVisible) => !isVisible);
  }

  protected toggleConfirmPasswordVisibility(): void {
    this.showConfirmPassword.update((isVisible) => !isVisible);
  }

  protected onSaveClick(): void {
    if (!this.canSubmit()) {
      return;
    }

    this.save.emit();
    this.confirmPassword.set('');
  }
}

